import { useCallback } from "react";
import { profileApi } from "../../../../api";
import { useGlobalNotifications } from "../../../../hooks/use-global-notifications";
import { isNative } from "../../../../platform";
import { ensureStoragePermission } from "../../../../platform/storage-permission";
import { emitPermissionAlert } from "../../../../contexts/permission-alert-context";

export const usePrivacySettings = () => {
    const { showError, showSuccess, showLoading, hideNotification } =
        useGlobalNotifications();

    const handleExportData = useCallback(async () => {
        try {
            showLoading("Preparing your data export...");

            // Call the API to get the ZIP file
            const zipBlob = await profileApi.exportData();
            const fileName = `mailander_data_export_${new Date().toISOString().split("T")[0]}.zip`;

            if (isNative()) {
                const granted = await ensureStoragePermission();
                if (!granted) {
                    hideNotification();
                    emitPermissionAlert('storage');
                    return;
                }

                showLoading("Saving to device...");

                const { Filesystem, Directory } = await import("@capacitor/filesystem");

                // Read blob as base64 via FileReader (reliable on iOS WebView)
                const base64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const result = reader.result as string;
                        // Strip the data:…;base64, prefix
                        const idx = result.indexOf(",");
                        resolve(idx >= 0 ? result.slice(idx + 1) : result);
                    };
                    reader.onerror = () => reject(reader.error);
                    reader.readAsDataURL(zipBlob);
                });

                await Filesystem.writeFile({
                    path: fileName,
                    data: base64,
                    directory: Directory.Documents,
                    recursive: true,
                });

                hideNotification();
                showSuccess("Saved to Files → Mailander");
            } else {
                hideNotification();

                // Web: trigger download via anchor click
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showSuccess("Data export downloaded successfully");
            }
        } catch (error) {
            console.error("Failed to export data:", error);
            hideNotification();
            showError("Failed to export your data. Please try again later.");
        }
    }, [showError, showSuccess, showLoading, hideNotification]);

    // const handleDeleteData = useCallback(() => {
    //     // TODO: Replace with real delete logic (API call, confirmation modal, etc.)
    //     // Example:
    //     // if (window.confirm('Are you sure you want to delete your data?')) {
    //     //   await api.deleteConversationHistory();
    //     // }
    //     console.log("Delete data clicked");
    // }, []);

    return {
        handleExportData,
        // handleDeleteData,
    };
};
